/**
 * Turning an id into a person, in one place.
 *
 * The pager stores ids and renders them: "acknowledged by
 * 01M1YRYH8RJWK43YTWB156Z4VP" on the incident card, a rotation's order as a
 * row of ULIDs, and "on call right now" as another one. The id is the right
 * thing to store — it is stable, it survives a rename, and it is what every
 * record in the kernel is keyed by — and it is the wrong thing to put in front
 * of somebody at three in the morning, which is the whole of LO-107.
 *
 * It lives here rather than in a screen because three screens need it and the
 * fourth already grew its own copy. A second implementation of "who is this"
 * is a second answer to it.
 *
 * ## Resolved from the directory, not from a table this app keeps
 *
 * `/oncall/people` is the kernel's list of people tagged `employee`, which is
 * the same list a rotation can name and the same list a page can reach. So a
 * name shown here is the name the directory holds, and a person the directory
 * drops stops resolving rather than lingering as a copy this app made.
 */

// No `.ts` on the specifier: a TypeScript module importing one needs
// `allowImportingTsExtensions`, which this project does not set. The `.jsx`
// screens carry it because they are not typechecked and Vite wants the hint.
import { api } from './oncall-api';
import { useEffect, useState } from 'react';

export interface Person {
  id: string;
  displayName?: string | null;
  phones?: string[];
}

/**
 * An id nobody in the directory answers to.
 *
 * Three ways to get here and a screen cannot tell them apart, which is why
 * the sentence does not guess: the person left, the person is not tagged
 * `employee`, or the id is a Cognito `sub` — the API falls back to one when
 * the directory cannot match the caller, so an acknowledgement can carry a
 * `sub` that will never appear in this list.
 *
 * What they have in common is the only thing worth saying: this app cannot
 * put a name to it.
 */
export const UNKNOWN = 'somebody this app cannot name';

/** Shown while the directory is on its way, in place of the id. */
export const LOADING = '…';

/**
 * One read per page load, shared by every screen that mounts.
 *
 * Not a cache with an expiry. The directory changes on a connector's
 * schedule, a page load is short, and a second request per screen for the
 * same eight rows is three requests to say the same thing.
 */
let inFlight: Promise<Person[]> | null = null;

function load(): Promise<Person[]> {
  inFlight ??= api.people()
    .then((body: any) => (body.people ?? []) as Person[])
    .catch((e: unknown) => {
      // Cleared, so a screen mounted after a failure tries again rather than
      // inheriting it forever. A directory that was briefly unreachable is
      // not a directory this tab has to give up on.
      inFlight = null;
      throw e;
    });
  return inFlight;
}

export interface Directory {
  people: Person[];
  /** A display name, or a sentence saying there is not one. */
  nameOf: (id: string | null | undefined) => string;
  /** Whether `nameOf` found a person, for a caller that styles the two apart. */
  knows: (id: string | null | undefined) => boolean;
  loading: boolean;
  /** The directory could not be read. Names fall back to ids — see below. */
  failure: string | null;
}

/**
 * The four answers, as a function of what is known — separated from the hook
 * so the branching can be tested without a renderer.
 *
 * They are four rather than two because "no name" has three causes and only
 * one of them is about the person: still loading, the directory could not be
 * read at all, and the directory was read and does not hold this id. Saying
 * the same thing about all three is how a screen ends up claiming somebody
 * left when the request simply has not landed.
 */
export function nameFrom(
  people: Person[] | null,
  id: string | null | undefined,
  failure: string | null,
): string {
  if (!id) return UNKNOWN;
  const found = people?.find((p) => p.id === id);
  if (found?.displayName) return found.displayName;
  // The id itself, but only when the directory could not be read at all. That
  // is a different failure from "this id is not a person here", and showing
  // the raw id is better than a confident sentence about somebody when the
  // list that would have named them never arrived.
  if (failure) return id;
  // Still loading. An id flashing on screen for one frame and then becoming a
  // name is the bug this module exists to remove, so the in-between state
  // says nothing rather than saying the id.
  if (people === null) return LOADING;
  return UNKNOWN;
}

export function useDirectory(): Directory {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    load()
      .then((found) => { if (live) setPeople(found); })
      .catch((e: any) => { if (live) { setPeople([]); setFailure(e.message); } });
    return () => { live = false; };
  }, []);

  return {
    people: people ?? [],
    loading: people === null,
    failure,
    knows: (id) => Boolean(id && people?.some(
      (p) => p.id === id && p.displayName)),
    nameOf: (id) => nameFrom(people, id, failure),
  };
}
